import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import {
  fetchAccounts,
  fetchActiveArtists,
  fetchArtist,
  fetchArtists,
  fetchAudioSafeUrl,
  fetchCapacity,
  fetchComments,
  fetchContent,
  fetchCurrentUser,
  fetchDiscover,
  fetchExperience,
  fetchExperienceRSVPs,
  fetchFeatured,
  fetchIdeas,
  fetchInvites,
  fetchMusic,
  fetchMutedComments,
  fetchPollResults,
  fetchScheduled,
  fetchBroadcast,
  fetchSubscriptions,
  fetchUser,
  fetchUserComments,
  fetchUserSummary,
  postAiSummary,
} from "./api";

const audioSafeUrlQueryKey = (contentKey) => ["audio_safe_url", contentKey];

const audioSafeUrlQuery = (contentKey) => ({
  queryKey: audioSafeUrlQueryKey(contentKey),
  queryFn: () => fetchAudioSafeUrl(contentKey),
  staleTime: Infinity, // permanent CDN URL, never needs refetch
  gcTime: Infinity,
  retry: 1,
});

export const useAudioSafeUrl = (contentKey) => {
  return useQuery({
    ...audioSafeUrlQuery(contentKey),
    enabled: !!contentKey,
  });
};

// Returns a getter that synchronously reads a prefetched (usePrefetchAudioUrls)
// audio URL from the query cache. Lets callers start playback for an arbitrary
// track inside a user/media-key gesture without awaiting a fetch.
export const useCachedAudioUrl = () => {
  const queryClient = useQueryClient();
  return (contentKey) =>
    contentKey
      ? queryClient.getQueryData(audioSafeUrlQueryKey(contentKey))
      : undefined;
};

// Warm the per-user watermarked URL for each track as soon as the list loads,
// so the (up to ~10s) cold watermark happens while the user browses and playback
// is instant on click. Cached results are reused by useAudioSafeUrl at play time.
export const usePrefetchAudioUrls = (content) => {
  const queryClient = useQueryClient();
  useEffect(() => {
    if (!content?.length) return;
    content.forEach((item) => {
      if (!item?.key) return;
      queryClient.prefetchQuery(audioSafeUrlQuery(item.key));
    });
  }, [content, queryClient]);
};

export const useAiSummary = (prompt) => {
  return useQuery({
    queryKey: ["ai_summary", prompt],
    queryFn: () => postAiSummary(prompt),
  });
};

export const useCurrentUser = () => {
  return useQuery({
    queryKey: ["current_user"],
    queryFn: () => fetchCurrentUser(),
  });
};
export const useArtist = (artistKey) => {
  return useQuery({
    queryKey: ["artist", artistKey],
    queryFn: () => fetchArtist(artistKey),
    enabled: !!artistKey,
  });
};

export const useFeatured = (artistKey) => {
  return useQuery({
    queryKey: ["featured", artistKey],
    queryFn: () => fetchFeatured(artistKey),
    enabled: !!artistKey,
    staleTime: 30 * 1000,
    refetchInterval: 30 * 1000,
  });
};

export const useNotes = (artistKey) => {
  const contentTypes = [
    "note",
    "poll",
    "image",
    "video",
    "carousel",
    "audio",
    "experience",
    "live",
    "merch",
  ];
  return useQuery({
    queryKey: ["notes", artistKey],
    queryFn: () => fetchContent(artistKey, contentTypes),
    enabled: !!artistKey,
    staleTime: 30 * 1000,
    refetchInterval: 30 * 1000,
  });
};

export const useMedia = (artistKey) => {
  const contentTypes = ["image", "video", "carousel"];
  return useQuery({
    queryKey: ["media", artistKey],
    queryFn: () => fetchContent(artistKey, contentTypes),
    enabled: !!artistKey,
    staleTime: 30 * 1000,
    refetchInterval: 30 * 1000,
  });
};

export const useMusic = (artistKey) => {
  const contentTypes = ["audio"];
  return useQuery({
    queryKey: ["music", artistKey],
    queryFn: () => fetchMusic(artistKey),
    enabled: !!artistKey,
    staleTime: 30 * 1000,
    // refetchInterval: 30 * 1000, // revisit this
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });
};

export const useThreads = (artistKey) => {
  const contentTypes = ["thread"];
  return useQuery({
    queryKey: ["threads", artistKey],
    queryFn: () => fetchContent(artistKey, contentTypes),
    enabled: !!artistKey,
    staleTime: 30 * 1000,
    refetchInterval: 30 * 1000,
  });
};

export const useMerch = (artistKey) => {
  const contentTypes = ["merch"];
  return useQuery({
    queryKey: ["merch", artistKey],
    queryFn: () => fetchContent(artistKey, contentTypes),
    enabled: !!artistKey,
    staleTime: 30 * 1000,
    refetchInterval: 30 * 1000,
  });
};

export const useExperiences = (artistKey) => {
  const contentTypes = ["experience"];
  return useQuery({
    queryKey: ["experiences", artistKey],
    queryFn: () => fetchContent(artistKey, contentTypes),
    enabled: !!artistKey,
    staleTime: 30 * 1000,
    refetchInterval: 30 * 1000,
  });
};

export const useActiveArtists = () => {
  return useQuery({
    queryKey: ["active_artists"],
    queryFn: () => fetchActiveArtists(),
  });
};

export const useDiscover = () => {
  return useQuery({
    queryKey: ["discover"],
    queryFn: () => fetchDiscover(),
  });
};

export const useSubscriptions = () => {
  return useQuery({
    queryKey: ["subscriptions"],
    queryFn: () => fetchSubscriptions(),
  });
};

export const useArtists = () => {
  return useQuery({
    queryKey: ["artists"],
    queryFn: () => fetchArtists(),
  });
};

export const useCapacity = (artistKey) => {
  return useQuery({
    queryKey: ["capacity", artistKey],
    queryFn: () => fetchCapacity(artistKey),
    enabled: !!artistKey,
  });
};

export const useInvites = (artistKey) => {
  return useQuery({
    queryKey: ["invites", artistKey],
    queryFn: () => fetchInvites(artistKey),
    enabled: !!artistKey,
  });
};

export const usePollResults = (contentKey) => {
  return useQuery({
    queryKey: ["poll_results", contentKey],
    queryFn: () => fetchPollResults(contentKey),
  });
};

export const useIdeas = () => {
  return useQuery({
    queryKey: ["ideas"],
    queryFn: () => fetchIdeas(),
  });
};

export const useScheduled = () => {
  return useQuery({
    queryKey: ["scheduled"],
    queryFn: () => fetchScheduled(),
  });
};

export const useExperience = (key) => {
  return useQuery({
    queryKey: ["experience", key],
    queryFn: () => fetchExperience(key),
  });
};

export const useExperienceRSVPs = (key) => {
  return useQuery({
    queryKey: ["experience_rsvps", key],
    queryFn: () => fetchExperienceRSVPs(key),
  });
};

export const useAccounts = () => {
  return useQuery({
    queryKey: ["accounts"],
    queryFn: () => fetchAccounts(),
  });
};



export const useUser = (key) => {
  return useQuery({
    queryKey: ["user", key],
    queryFn: () => fetchUser(key),
  });
};

export const useUserComments = (key) => {
  return useQuery({
    queryKey: ["user_comments", key],
    queryFn: () => fetchUserComments(key),
  });
};

export const useBroadcast = (contentKey) => {
  return useQuery({
    queryKey: ["broadcast", contentKey],
    queryFn: () => fetchBroadcast(contentKey),
    enabled: !!contentKey,
    refetchInterval: 5000,
  });
};

export const useComments = (contentKey) => {
  return useQuery({
    queryKey: ["comments", contentKey],
    queryFn: () => fetchComments(contentKey),
    enabled: !!contentKey,
    refetchInterval: 5000,
  });
};

export const useUserSummary = (userKey) => {
  return useQuery({
    queryKey: ["user_summary", userKey],
    queryFn: () => fetchUserSummary(userKey),
  });
};

export const useMutedComments = (artistKey) => {
  return useQuery({
    queryKey: ["muted_comments", artistKey],
    queryFn: () => fetchMutedComments(artistKey),
    enabled: !!artistKey,
  });
};
